import React, { useEffect } from 'react'
import { useAppDispatch } from 'app/hooks'
import { Dashboard } from 'schemas/DashboardSchema'
import { TopicInfo } from 'schemas/TopicInfoSchema'
import MetricTimeUnit from 'enums/MetricTimeUnit'
import MeasureType from 'enums/MeasureType'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import { createMetric } from 'utils/runSearchUtil'
import { toFilter, getDateRangeByType } from 'utils/widgetUtil'
import { getMetricSeries } from 'utils/measureTypeUtil'
import { date } from 'utils/dateUtil'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

import { SummaryMetricsCompactWidgetsType } from '../../types'

const isStartDateOutOfRange = (selectedTopic: TopicInfo, startDate: number): boolean => {
  const { start: topicStartDate } = getDateRangeByType(
    selectedTopic.timeInterval!,
    undefined,
    new Date(selectedTopic.fromDate!),
    new Date(selectedTopic.toDate!)
  )
  return startDate < topicStartDate - date.MS_IN_DAY
}

export const createSearches = (
  widgetSetting: SummaryMetricsCompactWidgetsType,
  dashboardData?: Dashboard,
  isAudienceQuery?: boolean
) => {
  const { precision, selectedTopic, measures } = widgetSetting

  const selectedPeriodFilter = toFilter(widgetSetting)

  const period = selectedPeriodFilter.publishedDate.end! - selectedPeriodFilter.publishedDate.start!
  const prevPeriodStart = selectedPeriodFilter.publishedDate.start! - period
  const prevPeriodEnd = selectedPeriodFilter.publishedDate.start! - 1

  const isPrevPeriodOutOfRange = isStartDateOutOfRange(selectedTopic, prevPeriodStart)

  /*
   * "mentions" is a mandatory measure for early handle the "no data" scenario.
   * Therefore, it should be added if it's not selected by the user.
   */
  const selectedMeasures = measures?.includes(MeasureType.MENTIONS)
    ? measures
    : [MeasureType.MENTIONS, ...measures!]

  const searches = []
  const selectedPeriodSearch = createMetric(
    dashboardData?.id || -1,
    dashboardData?.name || '',
    precision!,
    [selectedTopic.topicId!],
    selectedPeriodFilter,
    selectedMeasures!.flatMap((measure) => getMetricSeries(measure!)),
    [MetricTimeUnit.ALL],
    { isAudienceQuery }
  )
  searches.push(selectedPeriodSearch)

  if (widgetSetting.showChange && !isPrevPeriodOutOfRange) {
    const prevPeriodFilter = toFilter(widgetSetting)
    prevPeriodFilter.publishedDate.start = prevPeriodStart
    prevPeriodFilter.publishedDate.end = prevPeriodEnd

    const prevPeriodSearch = createMetric(
      dashboardData?.id || -1,
      dashboardData?.name || '',
      precision!,
      [selectedTopic.topicId!],
      prevPeriodFilter,
      selectedMeasures!.flatMap((measure) => getMetricSeries(measure!)),
      [MetricTimeUnit.ALL],
      { isAudienceQuery }
    )
    searches.push(prevPeriodSearch)
  }

  return searches
}

const RunSearchEffect = ({
  widgetId,
  isAudienceQuery,
  children
}: {
  widgetId: number
  isAudienceQuery?: boolean
  children: React.ReactElement
}) => {
  const widgetSetting = useDashboardWidgetSetting<SummaryMetricsCompactWidgetsType>(widgetId)
  const dashboardData = useDashboardData()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const searches = createSearches(widgetSetting, dashboardData!, isAudienceQuery)

    dispatch(
      getWidgetForDashboard({
        searches,
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp
      })
    )
  }, [widgetSetting, dashboardData, dispatch, isAudienceQuery, refreshTimestamp])

  return children
}

export default RunSearchEffect
